import React, { useState, useEffect, useCallback } from 'react';
import { Menu, Search, LogOut } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import CandidateCards from './components/CandidateCards';
import CandidateDetail from './components/CandidateDetail';
import AddCandidateModal from './components/AddCandidateModal';
import Sidebar, { NavModule } from './components/Sidebar';
import LoginPage from './components/LoginPage';
import TeamManager from './components/TeamManager';
import SystemSettings from './components/SystemSettings';
import FeishuNotifyModal from './components/FeishuNotifyModal';
import BatchImportModal from './components/BatchImportModal';
import NeedsList from './components/NeedsList';
import NeedDetail from './components/NeedDetail';
import NotificationCenter from './components/NotificationCenter';
import CreateNeedModal from './components/CreateNeedModal';
import HomePage from './components/HomePage';
import PositionManager from './components/PositionManager';
import PositionDetail from './components/PositionDetail';
import OnboardingManager from './components/OnboardingManager';
import OfferApprovalPage from './components/OfferApprovalPage';
import TalentHub from './components/TalentHub';
import EhrPage from './components/EhrPage';
import BossBrief from './components/BossBrief';
import ReferralPage from './components/ReferralPage';
import ReferralDashboard from './components/ReferralDashboard';
import DashboardPage from './components/DashboardPage';
import EstablishmentPage from './components/EstablishmentPage';
import ItResourcePage from './components/ItResourcePage';
import CareerPage from './components/CareerPage';
import WebsiteApplicants from './components/WebsiteApplicants';
import CandidateShareView from './components/CandidateShareView';
import HeadhunterPage from './components/HeadhunterPage';
import ReferralPosterPage from './components/ReferralPosterPage';
import { getCandidates, getPositions, getTags, getMe, logout as apiLogout } from './api';
import { Candidate, Position, Tag, User } from './types';
import './index.css';

function App() {
  // Public career page - no auth needed, render before any hooks
  const isCareerPage = window.location.pathname.startsWith('/career/');
  if (isCareerPage) return <CareerPage />;

  // Public headhunter page - token auth
  const isHeadhunterPage = window.location.pathname.startsWith('/headhunter/');
  if (isHeadhunterPage) return <HeadhunterPage />;

  // Public candidate share page - no auth needed
  const isSharePage = window.location.pathname.startsWith('/share/');
  if (isSharePage) return <CandidateShareView />;

  // Public referral poster page - employee self-service
  const isReferralPoster = window.location.pathname.startsWith('/referral-poster');
  if (isReferralPoster) return <ReferralPosterPage />;

  return <MainApp />;
}

function MainApp() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<string>('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTeamManager, setShowTeamManager] = useState(false);
  const [feishuNotifyCandidates, setFeishuNotifyCandidates] = useState<Candidate[] | null>(null);
  const [batchImportPositionId, setBatchImportPositionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalCandidateCount, setTotalCandidateCount] = useState<number>(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Navigation
  const [activeModule, setActiveModule] = useState<NavModule>('home');
  const [selectedNeedId, setSelectedNeedId] = useState<string | null>(null);
  const [headcountTab, setHeadcountTab] = useState<string | undefined>(undefined);
  const [headcountRequestId, setHeadcountRequestId] = useState<string | undefined>(undefined);
  // 岗位详情页：activeModule 仍为 positions，detailPositionId 非空时渲染详情而非列表
  const [detailPositionId, setDetailPositionId] = useState<string | null>(null);
  const [detailPositionTab, setDetailPositionTab] = useState<string | undefined>(undefined);
  const [offerApprovalCandidate, setOfferApprovalCandidate] = useState<string | null>(null);
  const [headcountSlotSearch, setHeadcountSlotSearch] = useState<string | undefined>(undefined);
  const [showCreateNeed, setShowCreateNeed] = useState(false);
  const [needsRefreshKey, setNeedsRefreshKey] = useState(0);
  const [currentStageCandidateIds, setCurrentStageCandidateIds] = useState<string[]>([]);
  const [homeCandidateIds, setHomeCandidateIds] = useState<string[]>([]);
  const [pinnedPositionIds, setPinnedPositionIds] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('pinned_positions') || '[]'); } catch { return []; }
  });

  const togglePinPosition = (positionId: string) => {
    setPinnedPositionIds(prev => {
      const next = prev.includes(positionId) ? prev.filter(id => id !== positionId) : [...prev, positionId];
      localStorage.setItem('pinned_positions', JSON.stringify(next));
      return next;
    });
  };
  const [homeRefreshKey, setHomeRefreshKey] = useState(0);

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('ats_token');
    if (token) {
      getMe().then(user => {
        setCurrentUser(user);
        setAuthChecked(true);
      }).catch(() => {
        localStorage.removeItem('ats_token');
        localStorage.removeItem('ats_user');
        setAuthChecked(true);
      });
    } else {
      setAuthChecked(true);
    }
  }, []);

  const handleLogin = (user: User, token: string) => {
    setCurrentUser(user);
    // Enrich with effective permissions + position assignments
    getMe().then(full => setCurrentUser(full)).catch(() => {});
  };

  const handleLogout = async () => {
    try { await apiLogout(); } catch {}
    localStorage.removeItem('ats_token');
    localStorage.removeItem('ats_user');
    setCurrentUser(null);
    setCandidates([]);
    setPositions([]);
    setTags([]);
    setSelectedId(null);
  };

  const fetchCandidates = useCallback(async () => {
    try {
      const params: Record<string, string> = { limit: '10000' };
      if (selectedPositionId) params.position_id = selectedPositionId;
      if (searchQuery) params.search = searchQuery;
      if (stageFilter) params.stage = stageFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await getCandidates(params);
      setCandidates(data.candidates);
      // Track total count when viewing talent pool (no position filter)
      if (!selectedPositionId && !searchQuery && !stageFilter && !statusFilter) {
        setTotalCandidateCount(data.pagination?.total ?? data.candidates.length);
      }
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
    }
  }, [selectedPositionId, searchQuery, stageFilter, statusFilter]);

  const fetchPositions = useCallback(async () => {
    try {
      const data = await getPositions();
      setPositions(data);
    } catch (err) {
      console.error('Failed to fetch positions:', err);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const data = await getTags();
      setTags(data);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      Promise.all([fetchPositions(), fetchTags()]).then(() => setLoading(false));
      if (currentUser.role === 'referrer') setActiveModule('referral');
      if (currentUser.role === 'it') setActiveModule('it_resources');
    }
  }, [currentUser, fetchPositions, fetchTags]);

  useEffect(() => {
    if (!loading && currentUser) fetchCandidates();
  }, [fetchCandidates, loading, currentUser]);

  // Handle URL params: ?candidate=xxx opens candidate detail directly (from Feishu links)
  useEffect(() => {
    if (!loading && currentUser) {
      // Fall back to the query saved before a Feishu OAuth redirect (which drops the query string)
      let search = window.location.search;
      const pending = sessionStorage.getItem('ats_pending_query');
      if (pending && !new URLSearchParams(search).get('candidate')) {
        search = pending;
      }
      sessionStorage.removeItem('ats_pending_query');
      const params = new URLSearchParams(search);
      const moduleParam = params.get('module');
      const candidateId = params.get('candidate');
      // ?module=offer_approval[&candidate=<id>] 直达 Offer 审批页（供飞书通知等外部链接使用），优先于候选人详情
      if (moduleParam === 'offer_approval') {
        setOfferApprovalCandidate(candidateId || null);
        setActiveModule('offer_approval');
        window.history.replaceState({}, '', window.location.pathname);
      } else if (candidateId) {
        setActiveModule('candidates');
        setSelectedId(candidateId);
        // Clean URL without reload
        window.history.replaceState({}, '', window.location.pathname);
      }
      if (moduleParam === 'headcount') {
        const tabParam = params.get('tab');
        const reqId = params.get('request_id');
        setHeadcountTab(tabParam || undefined);
        setHeadcountRequestId(reqId || undefined);
        setActiveModule('headcount');
        window.history.replaceState({}, '', window.location.pathname);
      }
      // ?module=positions&position=<id>&tab=<tab> 直达岗位详情（供飞书通知等外部链接使用）
      if (moduleParam === 'positions') {
        const posId = params.get('position');
        setDetailPositionId(posId || null);
        setDetailPositionTab(params.get('tab') || undefined);
        setActiveModule('positions');
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [loading, currentUser]);

  // Auth check spinner
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  // Not logged in
  if (!currentUser) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <Toaster position="top-right" toastOptions={{ duration: 3000, style: { background: '#363636', color: '#fff', fontSize: '14px' } }} />
      </>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-white">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {(() => {
        const isHome = activeModule === 'home';
        const greeting = (() => {
          const h = new Date().getHours();
          if (h < 12) return '上午好';
          if (h < 18) return '下午好';
          return '晚上好';
        })();
        const bell = (
          <NotificationCenter
            onNavigate={(link) => {
              if (link.startsWith('?candidate=')) {
                const cid = link.replace('?candidate=', '');
                setActiveModule('candidates');
                setSelectedId(cid);
              } else if (link.startsWith('?need=')) {
                const nid = link.replace('?need=', '');
                setActiveModule('needs');
                setSelectedNeedId(nid);
              } else if (link.startsWith('?module=offer_approval')) {
                const cid = link.match(/candidate=([^&]+)/);
                setOfferApprovalCandidate(cid ? cid[1] : null);
                setActiveModule('offer_approval');
              } else if (link.startsWith('?module=headcount')) {
                const m = link.match(/tab=([a-z]+)/);
                const rid = link.match(/request_id=([a-f0-9-]+)/);
                setHeadcountTab(m ? m[1] : undefined);
                setHeadcountRequestId(rid ? rid[1] : undefined);
                setActiveModule('headcount');
              }
            }}
          />
        );
        const renderSidebar = (opts?: { hideBrand?: boolean; variant?: 'default' | 'card'; hideUserFooter?: boolean }) => (
          <Sidebar
            activeModule={activeModule}
            onModuleChange={(m) => { setActiveModule(m); setSelectedId(null); setSelectedNeedId(null); setFavoritesOnly(false); setSidebarOpen(false); }}
            currentUser={currentUser}
            onLogout={handleLogout}
            onOpenTeamManager={() => { if (currentUser.role === 'admin') setShowTeamManager(true); setSidebarOpen(false); }}
            positions={positions}
            selectedPositionId={selectedPositionId}
            onPositionChange={(id) => { setSelectedPositionId(id); setSelectedId(null); setFavoritesOnly(false); }}
            totalCandidateCount={totalCandidateCount}
            onAddCandidate={() => setShowAddModal(true)}
            onBatchImport={(positionId) => setBatchImportPositionId(positionId)}
            pinnedPositionIds={pinnedPositionIds}
            onTogglePinPosition={togglePinPosition}
            onOpenWebsiteApplicants={() => { setActiveModule('website_applicants'); setSelectedPositionId(''); setSelectedId(null); setFavoritesOnly(false); setSidebarOpen(false); }}
            favoritesActive={favoritesOnly}
            onOpenFavorites={() => { setActiveModule('candidates'); setSelectedPositionId(''); setSelectedId(null); setFavoritesOnly(true); setSidebarOpen(false); }}
            hideBrand={opts?.hideBrand}
            variant={opts?.variant}
            hideUserFooter={opts?.hideUserFooter}
          />
        );
        const moduleContent = (
            <>
              {activeModule === 'home' && (
                <HomePage
                  currentUser={currentUser}
                  notificationBell={bell}
                  onNavigateCandidate={(id) => { setSelectedId(id); }}
                  onOpenCandidate={(id, cIds) => { setSelectedId(id); if (cIds) setHomeCandidateIds(cIds); }}
                  onNavigateToOnboarding={() => setActiveModule('onboarding')}
                  refreshKey={homeRefreshKey}
                  hideHeader
                />
              )}

              {activeModule === 'candidates' && (
                <CandidateCards
                  candidates={candidates}
                  onSelect={setSelectedId}
                  positionName={positions.find(p => p.id === selectedPositionId)?.name}
                  currentPositionId={selectedPositionId || undefined}
                  isHr={currentUser.role === 'admin' || currentUser.role === 'hr' || currentUser.role === 'vip'}
                  onFeishuNotify={(selected: Candidate[]) => setFeishuNotifyCandidates(selected)}
                  onUpdate={() => { fetchCandidates(); fetchPositions(); }}
                  notificationBell={bell}
                  onStageChange={setCurrentStageCandidateIds}
                  positions={positions}
                  searchQuery={searchQuery}
                  onSearch={(q) => { setSearchQuery(q); }}
                  favoritesOnly={favoritesOnly}
                  onFavoritesOnlyChange={setFavoritesOnly}
                />
              )}

              {activeModule === 'needs' && !selectedNeedId && (
                <NeedsList
                  currentUser={currentUser}
                  onSelect={(id) => setSelectedNeedId(id)}
                  onCreateNew={() => setShowCreateNeed(true)}
                  refreshKey={needsRefreshKey}
                  notificationBell={bell}
                />
              )}

              {activeModule === 'needs' && selectedNeedId && (
                <NeedDetail
                  needId={selectedNeedId}
                  currentUser={currentUser}
                  onBack={() => setSelectedNeedId(null)}
                  onUpdate={() => { setNeedsRefreshKey(k => k + 1); fetchPositions(); }}
                  notificationBell={bell}
                />
              )}

              {/* 详情页打开时列表只隐藏不卸载，否则返回列表会丢掉 Tab / 页码 / 筛选条件，
                  表现为「关掉一个招聘中的岗位后跳回全部岗位第 1 页」 */}
              {activeModule === 'positions' && (
                <div className={detailPositionId ? 'hidden' : 'h-full'}>
                  <PositionManager
                    positions={positions}
                    onUpdate={fetchPositions}
                    currentUser={currentUser}
                    notificationBell={bell}
                    onOpenPosition={(id, tab) => { setDetailPositionId(id); setDetailPositionTab(tab); }}
                  />
                </div>
              )}

              {activeModule === 'positions' && detailPositionId && (
                <PositionDetail
                  positionId={detailPositionId}
                  currentUser={currentUser}
                  initialTab={detailPositionTab}
                  notificationBell={bell}
                  onBack={() => { setDetailPositionId(null); setDetailPositionTab(undefined); }}
                  onUpdate={fetchPositions}
                  onViewCandidates={(pid) => {
                    setSelectedPositionId(pid);
                    setDetailPositionId(null);
                    setActiveModule('candidates');
                  }}
                  onOpenCandidate={(cid) => setSelectedId(cid)}
                  // 联动：绑定 Tab 点编制编号 → 编制管理页并按编号搜索定位
                  onOpenSlot={(code) => {
                    setHeadcountSlotSearch(code);
                    // 编制管理页的编制列表 Tab key 是 'list'（normalizeTab 只认这个值）
                    setHeadcountTab('list');
                    setActiveModule('headcount');
                  }}
                />
              )}

              {activeModule === 'offer_approval' && (
                <OfferApprovalPage
                  currentUser={currentUser}
                  notificationBell={bell}
                  initialCandidateId={offerApprovalCandidate}
                  onConsumedInitialCandidate={() => setOfferApprovalCandidate(null)}
                  onOpenCandidate={(id) => setSelectedId(id)}
                />
              )}

              {activeModule === 'onboarding' && (
                <OnboardingManager
                  currentUser={currentUser}
                  notificationBell={bell}
                  onOpenCandidate={(id) => setSelectedId(id)}
                />
              )}

              {activeModule === 'it_resources' && (
                <ItResourcePage currentUser={currentUser} notificationBell={bell} />
              )}

              {activeModule === 'referral' && (
                ['admin', 'hr', 'vip'].includes(currentUser.role)
                  ? <ReferralDashboard currentUser={currentUser} notificationBell={bell} onOpenCandidate={(id) => setSelectedId(id)} />
                  : <ReferralPage currentUser={currentUser} notificationBell={bell} onOpenCandidate={(id) => setSelectedId(id)} />
              )}

              {activeModule === 'boss_brief' && (
                <BossBrief currentUser={currentUser} notificationBell={bell} onOpenCandidate={(id) => setSelectedId(id)} />
              )}

              {activeModule === 'talent_hub' && (
                <TalentHub currentUser={currentUser} notificationBell={bell} />
              )}

              {activeModule === 'ehr_main' && (
                <EhrPage currentUser={currentUser} section="main" notificationBell={bell} />
              )}
              {activeModule === 'ehr_data' && (
                <EhrPage currentUser={currentUser} section="data" notificationBell={bell} />
              )}
              {activeModule === 'ehr_system' && (
                <EhrPage currentUser={currentUser} section="system" notificationBell={bell} />
              )}

              {activeModule === 'headcount' && (
                <EstablishmentPage
                  currentUser={currentUser}
                  notificationBell={bell}
                  initialTab={headcountTab as any}
                  initialRequestId={headcountRequestId}
                  initialSlotSearch={headcountSlotSearch}
                  // 联动：编制列表点「对应岗位」→ 岗位详情
                  onOpenPosition={(id) => { setDetailPositionId(id); setDetailPositionTab(undefined); setActiveModule('positions'); }}
                />
              )}

              {activeModule === 'website_applicants' && (
                <WebsiteApplicants
                  currentUser={currentUser}
                  notificationBell={bell}
                  onOpenCandidate={(id) => { setActiveModule('candidates'); setSelectedId(id); }}
                />
              )}

              {activeModule === 'dashboard' && (
                <DashboardPage currentUser={currentUser} notificationBell={bell} />
              )}

              {activeModule === 'team_management' && currentUser.role === 'admin' && (
                <TeamManager
                  currentUser={currentUser}
                  positions={positions}
                  embedded
                />
              )}

              {activeModule === 'system_settings' && currentUser.role === 'admin' && (
                <SystemSettings currentUser={currentUser} />
              )}
            </>
        );

        // ===== HOME: 4-block floating layout =====
        if (isHome) {
          return (
            <div className="flex-1 min-h-0 flex p-2.5 gap-2.5">
              {/* Mobile sidebar drawer */}
              <div className={`
                fixed inset-y-0 left-0 z-50 w-56 transform transition-transform duration-200 ease-out md:hidden
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
              `}>
                {renderSidebar()}
              </div>

              {/* Left column: logo card + sidebar (same width) */}
              <div className="hidden md:flex flex-col gap-2.5 w-56 shrink-0 min-h-0">
                <div className="h-16 shrink-0 bg-white rounded-2xl border border-gray-200/80 flex items-center justify-center px-4">
                  <span className="font-bold text-[15px] tracking-tight text-gray-900 truncate">
                    ManifoldTech <span className="text-primary-600">HR</span>
                  </span>
                </div>
                <div className="flex-1 min-h-0">
                  {renderSidebar({ hideBrand: true, variant: 'card', hideUserFooter: true })}
                </div>
              </div>

              {/* Right column: header bar (same height as logo card) + content */}
              <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-2.5">
                <div className="h-16 shrink-0 bg-white rounded-2xl border border-gray-200/80 flex items-center gap-4 px-5 md:px-8">
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-50 shrink-0"
                  >
                    <Menu size={20} />
                  </button>
                  <div className="min-w-0 hidden sm:block shrink-0">
                    <div className="text-[15px] font-bold text-gray-900 truncate leading-none">
                      {greeting}，{currentUser.display_name || currentUser.username}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 max-w-md mx-auto w-full">
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-500" />
                      <input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedPositionId(''); setActiveModule('candidates'); } }}
                        placeholder="搜索候选人姓名、电话、公司…"
                        className="w-full h-9 pl-9 pr-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>
                  <div className="shrink-0">{bell}</div>
                  <div className="shrink-0 flex items-center gap-2 pl-2 md:pl-3 md:border-l border-gray-100">
                    <div className="w-9 h-9 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center text-sm font-bold shrink-0">
                      {(currentUser.display_name || currentUser.username || '?').charAt(0)}
                    </div>
                    <div className="hidden lg:block leading-tight">
                      <div className="text-sm font-semibold text-gray-900 truncate max-w-[120px]">{currentUser.display_name || currentUser.username}</div>
                      <div className="text-[11px] text-gray-400">
                        {currentUser.role === 'admin' ? '管理员' : currentUser.role === 'hr' ? 'HR' : currentUser.role === 'vip' ? 'VIP面试官' : currentUser.role === 'referrer' ? '内推官' : currentUser.role === 'it' ? 'IT资源官' : '面试官'}
                      </div>
                    </div>
                    <button
                      onClick={handleLogout}
                      title="退出登录"
                      className="ml-1 p-1.5 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors shrink-0"
                    >
                      <LogOut size={16} />
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-0 overflow-hidden">
                  {moduleContent}
                </div>
              </div>
            </div>
          );
        }

        // ===== FRAMED modules: 复用首页悬浮卡片框架 =====
        if (
          activeModule === 'positions' || activeModule === 'headcount' || activeModule === 'offer_approval' ||
          activeModule === 'candidates' || activeModule === 'it_resources' || activeModule === 'onboarding' ||
          activeModule === 'ehr_main' || activeModule === 'ehr_data' || activeModule === 'ehr_system'
        ) {
          return (
            <div className="flex-1 min-h-0 flex p-2.5 gap-2.5">
              {/* Mobile sidebar drawer */}
              <div className={`
                fixed inset-y-0 left-0 z-50 w-56 transform transition-transform duration-200 ease-out md:hidden
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
              `}>
                {renderSidebar()}
              </div>

              {/* Left column: logo card + sidebar card */}
              <div className="hidden md:flex flex-col gap-2.5 w-56 shrink-0 min-h-0">
                <div className="h-16 shrink-0 bg-white rounded-2xl border border-gray-200/80 flex items-center justify-center px-4">
                  <span className="font-bold text-[15px] tracking-tight text-gray-900 truncate">
                    ManifoldTech <span className="text-primary-600">HR</span>
                  </span>
                </div>
                <div className="flex-1 min-h-0">
                  {renderSidebar({ hideBrand: true, variant: 'card' })}
                </div>
              </div>

              {/* Right column: module content */}
              <div className="flex-1 min-w-0 min-h-0 flex flex-col">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="md:hidden mb-2 self-start p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  <Menu size={20} />
                </button>
                <div className="flex-1 min-h-0">
                  {moduleContent}
                </div>
              </div>
            </div>
          );
        }

        // ===== DEFAULT: sidebar + main =====
        return (
          <>
            <div className={`
              fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-out md:relative md:translate-x-0 md:z-auto
              ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
              {renderSidebar()}
            </div>
            <main className="flex-1 overflow-hidden relative">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden fixed top-3 left-3 z-30 p-2 rounded-lg bg-white shadow-md border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                <Menu size={20} />
              </button>
              {moduleContent}
            </main>
          </>
        );
      })()}

      {/* Candidate Detail Modal Overlay */}
      {selectedId && (
        <CandidateDetail
          candidateId={selectedId}
          positions={positions}
          allTags={tags}
          currentUser={currentUser}
          onUpdate={() => { fetchCandidates(); }}
          onTagsChange={fetchTags}
          onBack={() => { setSelectedId(null); if (activeModule === 'home') setHomeRefreshKey(k => k + 1); }}
          candidateIds={activeModule === 'home' && homeCandidateIds.length > 0 ? homeCandidateIds : currentStageCandidateIds.length > 0 ? currentStageCandidateIds : candidates.map(c => c.id)}
          onNavigate={(id) => setSelectedId(id)}
        />
      )}

      {showAddModal && (
        <AddCandidateModal
          positions={positions}
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); fetchCandidates(); }}
        />
      )}

      {showTeamManager && currentUser.role === 'admin' && (
        <TeamManager
          currentUser={currentUser}
          positions={positions}
          onClose={() => setShowTeamManager(false)}
        />
      )}

      {batchImportPositionId && (
        <BatchImportModal
          positionId={batchImportPositionId}
          positionName={positions.find(p => p.id === batchImportPositionId)?.name || '未知岗位'}
          // 只能往「招聘中」的岗位批量导入；当前入口岗位始终保留，避免下拉 value 匹配不上
          positions={positions
            .filter(p => p.status === 'open' || p.id === batchImportPositionId)
            .map(p => ({ id: p.id, name: p.name }))}
          onClose={() => setBatchImportPositionId(null)}
          onImported={() => { fetchCandidates(); fetchPositions(); }}
        />
      )}

      {feishuNotifyCandidates && (
        <FeishuNotifyModal
          candidates={feishuNotifyCandidates}
          autoAssignPositionId={selectedPositionId || undefined}
          autoAssignPositionName={positions.find(p => p.id === selectedPositionId)?.name}
          onClose={() => setFeishuNotifyCandidates(null)}
          onSent={() => { setFeishuNotifyCandidates(null); fetchCandidates(); fetchPositions(); }}
        />
      )}

      {showCreateNeed && (
        <CreateNeedModal
          currentUser={currentUser}
          onClose={() => setShowCreateNeed(false)}
          onCreated={(id) => { setShowCreateNeed(false); setSelectedNeedId(id); setNeedsRefreshKey(k => k + 1); }}
        />
      )}

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: { background: '#363636', color: '#fff', fontSize: '14px' },
        }}
      />
    </div>
  );
}

export default App;
